import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useFetch } from '@reveni-io/reveni-ui'

import { noPaginationParams } from '@/common/constants'
import { getBlockName } from '@/configuration/pages/ConfigurationPage/OrderTrackingPageConfiguration/TrackingPageEditorModal/helpers'
import configurationServices from '@/configuration/services/services'

import { initBlockVisibility } from './constants'
import { buildBlockVisibilityFromSaved, getBlockVisibilitySyncActions, haveBlockVisibilityChanged } from './helpers'
import { transformRequestEditAdvancedBlock, transformResponseAdvancedBlocks } from './transformers'

const useBlockVisibility = ({ storeId, layout, blockSettings, timeZone }) => {
  const { t } = useTranslation()

  const { data: savedBlocks, fetchData: fetchAdvancedBlocks } = useFetch(configurationServices.getAdvancedBlocks, {
    doInitialRequest: true,
    params: { storeId, ...noPaginationParams },
    transformResponse: transformResponseAdvancedBlocks,
  })

  const { fetchData: editAdvancedBlock } = useFetch(configurationServices.editAdvancedBlock, {
    doInitialRequest: false,
    transformRequest: transformRequestEditAdvancedBlock,
  })

  const { fetchData: deleteAdvancedBlock } = useFetch(configurationServices.deleteAdvancedBlock, {
    doInitialRequest: false,
  })

  const savedVisibility = useMemo(() => savedBlocks && buildBlockVisibilityFromSaved(savedBlocks), [savedBlocks])

  const [blockVisibility, setBlockVisibility] = useState({})

  useEffect(() => {
    if (savedVisibility) setBlockVisibility(savedVisibility)
  }, [savedVisibility])

  const getBlockVisibility = blockId => blockVisibility[blockId] || initBlockVisibility

  const updateBlockVisibility = (blockId, changes) => {
    setBlockVisibility(previous => {
      return { ...previous, [blockId]: { ...(previous[blockId] || initBlockVisibility), ...changes } }
    })
  }

  const resetBlockVisibility = blockId => {
    setBlockVisibility(previous => ({ ...previous, [blockId]: initBlockVisibility }))
  }

  const hasBlockVisibilityChanges =
    Boolean(layout) &&
    Boolean(savedVisibility) &&
    haveBlockVisibilityChanged({ layout, blockVisibility, savedVisibility, timeZone })

  const syncBlock = async (blockUid, request) => {
    try {
      await request()
    } catch (error) {
      error.errorMessage = t('No se ha podido guardar la visibilidad del bloque {{name}}', {
        name: getBlockName(blockUid, blockSettings, t),
      })
      throw error
    }
  }

  const saveBlockVisibility = async () => {
    const { updates, deletions } = getBlockVisibilitySyncActions({ layout, blockVisibility, savedVisibility, timeZone })

    await Promise.all([
      ...updates.map(({ blockUid, payload }) => {
        return syncBlock(blockUid, () => editAdvancedBlock({ storeId, blockUid, payload }))
      }),
      ...deletions.map(blockUid => syncBlock(blockUid, () => deleteAdvancedBlock({ storeId, blockUid }))),
    ])
  }

  const refreshBlockVisibility = () => fetchAdvancedBlocks({ storeId, ...noPaginationParams })

  return {
    blockVisibility,
    getBlockVisibility,
    updateBlockVisibility,
    resetBlockVisibility,
    hasBlockVisibilityChanges,
    saveBlockVisibility,
    refreshBlockVisibility,
  }
}

export default useBlockVisibility
